// src/components/dashboard/livegrid/liveGridIntel.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Live Grid intelligence (pure functions, no React)
//
//   normalizeLivePatch(event, data)  WebSocket / ws_message payload → per-asset patch
//   mergeRiskIntoAssets(assets, map)  ML "assets at risk" → asset fields (risk_*)
//   assessAsset(asset, now)           explainable priority score + reasons + advice
//   buildGridBrief(assets, opts)      grid score, health split, priorities, worst zones
//   answerGridLocally(q, brief)       rules-based answers when Ollama is offline
//   buildGridAiContext(brief, sel)    compact snapshot sent to Ollama (FlowSense AI)
// ─────────────────────────────────────────────────────────────────────────────

const DAY = 86400000;
const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
const lc = (v) => String(v ?? '').toLowerCase();
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

export const LIVE_ALERT_TTL_MS = 2 * 60 * 1000;

// Event names (backend + legacy) by what they mean for an asset on the map.
const STATUS_EVENTS = new Set(['asset_status_update', 'asset_status_changed', 'asset_updated', 'asset_update', 'utility_status_update', 'utility_updated']);
const HEALTH_EVENTS = new Set(['health_score_update', 'asset_prediction_update', 'predictive_risk_update']);
const READING_EVENTS = new Set(['new_pressure_reading', 'new_reading', 'sensor_reading', 'new_water_level_reading', 'telemetry_update']);
const ALERT_EVENTS = new Set(['anomaly_recorded', 'critical_anomaly_detected', 'critical_anomaly', 'new_sensor_alert', 'sensor_status_update']);
const WO_EVENTS = new Set(['work_order_events', 'work_order_status_updated', 'work_order_assigned', 'work_order_created', 'work_order_updated', 'work_order_completed', 'new_work_order_created']);

/** Names the Live Grid subscribes to directly (per-asset patches). */
export const LIVE_GRID_EVENTS = [...STATUS_EVENTS, ...HEALTH_EVENTS, ...READING_EVENTS, ...ALERT_EVENTS, ...WO_EVENTS];

/**
 * @returns {null | { kind, assetId, sensorId, patch, pulse, alert, workOrder }}
 *   kind: 'status' | 'health' | 'reading' | 'alert' | 'workOrder'
 */
export function normalizeLivePatch(eventName, raw) {
    if (!raw || typeof raw !== 'object') return null;
    const name = eventName || raw.type || raw.event;
    const p = (raw.payload && typeof raw.payload === 'object') ? raw.payload
        : (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) ? raw.data : raw;
    const assetId = p.asset_id ?? p.assetId ?? p.asset?.id ?? raw.asset_id ?? raw.assetId ?? null;
    const sensorId = p.sensor_id ?? p.sensorId ?? p.device_id ?? p.deviceId ?? null;
    const base = { assetId: assetId != null ? String(assetId) : null, sensorId: sensorId != null ? String(sensorId) : null, patch: {}, pulse: false, alert: null, workOrder: null };

    if (STATUS_EVENTS.has(name)) {
        const status = p.status ?? p.new_status ?? p.newStatus;
        if (!status || !base.assetId) return null;
        return { ...base, kind: 'status', patch: { status: String(status) } };
    }
    if (HEALTH_EVENTS.has(name)) {
        if (!base.assetId) return null;
        const patch = {};
        const health = num(p.health_score ?? p.healthScore);
        let risk = num(p.risk_score ?? p.riskScore);
        const prob = num(p.failure_probability ?? p.failureProbability);
        if (risk === null && prob !== null) risk = prob <= 1 ? prob * 100 : prob;
        const rul = num(p.remaining_useful_life ?? p.rul ?? p.rul_days ?? p.remainingUsefulLife);
        const pfd = p.predicted_failure_date ?? p.predictedFailureDate;
        if (health !== null) patch.health_score = Math.round(clamp(health, 0, 100));
        if (risk !== null) patch.risk_score = Math.round(clamp(risk, 0, 100));
        if (rul !== null) patch.remaining_useful_life = Math.round(rul);
        if (pfd) patch.predicted_failure_date = pfd;
        if (!Object.keys(patch).length) return null;
        return { ...base, kind: 'health', patch };
    }
    if (READING_EVENTS.has(name)) {
        const v = num(p.psi ?? p.pressure ?? p.value ?? p.reading ?? p.water_level);
        if (v === null || (!base.assetId && !base.sensorId)) return null;
        return { ...base, kind: 'reading', patch: { last_psi: v }, pulse: true };
    }
    if (ALERT_EVENTS.has(name)) {
        if (!base.assetId && !base.sensorId) return null;
        const severity = lc(p.severity || (name.startsWith('critical') ? 'critical' : 'high')) || 'high';
        return {
            ...base, kind: 'alert', pulse: true,
            alert: { severity, message: p.alert_message || p.message || p.description || 'Sensor anomaly detected', event: name, at: Date.now() },
        };
    }
    if (WO_EVENTS.has(name)) {
        const id = p.workOrderId ?? p.work_order_id ?? p.id ?? null;
        return { ...base, kind: 'workOrder', workOrder: { id, status: p.newStatus ?? p.status ?? p.type ?? null } };
    }
    return null;
}

/** Index of ML "assets at risk" rows by asset id (string keys). */
export function indexRisk(rows = []) {
    const map = {};
    (Array.isArray(rows) ? rows : []).forEach((r) => {
        const id = r?.asset_id ?? r?.assetId;
        if (id == null) return;
        const score = num(r.risk_score ?? r.riskScore) ?? 0;
        const prev = map[String(id)];
        if (prev && prev.risk_score >= score) return;
        map[String(id)] = {
            risk_score: Math.round(clamp(score, 0, 100)),
            risk_severity: lc(r.severity) || null,
            risk_description: r.description || null,
            prescriptive_action: r.prescriptive_action || null,
            risk_alert_id: r.id ?? null,
            risk_predicted_failure_date: r.predicted_failure_date ?? r.predictedFailureDate ?? null,
        };
    });
    return map;
}

/** Adds risk_* fields; keeps the same object when nothing changes (cheap re-renders). */
export function mergeRiskIntoAssets(assets = [], riskIndex = {}) {
    if (!riskIndex || !Object.keys(riskIndex).length) return assets;
    return assets.map((a) => {
        const r = riskIndex[String(a.id)];
        if (!r) return a;
        return { ...a, ...r, predicted_failure_date: a.predicted_failure_date || r.risk_predicted_failure_date || null };
    });
}

const daysUntil = (d, now) => {
    if (!d) return null;
    const t = new Date(d).getTime();
    return Number.isFinite(t) ? Math.ceil((t - now) / DAY) : null;
};

/**
 * Explainable priority for one asset.
 * @returns {{ score, level, reasons: string[], recommendation, suggestedPriority, suggestedType, failInDays }}
 */
export function assessAsset(asset = {}, now = Date.now(), liveAlert = null) {
    let score = 0;
    const reasons = [];
    const health = num(asset.health_score);
    const risk = num(asset.risk_score);
    const status = lc(asset.status);
    const rul = num(asset.remaining_useful_life);
    const failIn = daysUntil(asset.predicted_failure_date, now) ?? (rul !== null ? rul : null);

    if (health !== null) {
        if (health < 40) { score += 40; reasons.push(`Health ${health}% (critical)`); }
        else if (health < 80) { score += 15; reasons.push(`Health ${health}% (deteriorating)`); }
    }
    if (risk !== null) {
        if (risk >= 80) { score += 30; reasons.push(`ML failure risk ${risk}%`); }
        else if (risk >= 60) { score += 15; reasons.push(`ML failure risk ${risk}%`); }
    }
    if (failIn !== null) {
        if (failIn <= 7) { score += 30; reasons.push(failIn <= 0 ? 'Predicted failure date has passed' : `Predicted to fail in ${failIn} day${failIn === 1 ? '' : 's'}`); }
        else if (failIn <= 30) { score += 15; reasons.push(`Predicted to fail in ${failIn} days`); }
    }
    if (liveAlert) {
        const crit = liveAlert.severity === 'critical';
        score += crit ? 35 : 15;
        reasons.push(`Live ${crit ? 'critical ' : ''}anomaly: ${liveAlert.message}`);
    }
    if (status === 'critical') { score += 25; reasons.push('Status: critical'); }
    else if (status === 'offline' || status === 'inactive') { score += 15; reasons.push(`Status: ${status}`); }
    else if (status === 'warning') { score += 10; reasons.push('Status: warning'); }
    const covered = status === 'maintenance';
    if (covered) { score = Math.max(0, score - 20); reasons.push('A work order is already active'); }

    score = clamp(score, 0, 100);
    const level = score >= 60 ? 'critical' : score >= 30 ? 'high' : score >= 10 ? 'medium' : 'low';
    const rx = asset.prescriptive_action?.action;
    let recommendation;
    let suggestedType = 'inspection';
    if (covered) recommendation = 'Follow the active work order — no new job needed.';
    else if (liveAlert?.severity === 'critical' || status === 'critical') { recommendation = 'Dispatch a crew now (emergency / corrective) and verify the sensor reading on site.'; suggestedType = 'emergency'; }
    else if (rx === 'Replace') { recommendation = 'ML recommends replacement — raise a replacement work order and check stock.'; suggestedType = 'replacement'; }
    else if ((failIn !== null && failIn <= 30) || (risk !== null && risk >= 60) || (health !== null && health < 80)) { recommendation = 'Schedule predictive maintenance before the predicted failure.'; suggestedType = 'predictive'; }
    else if (status === 'offline' || status === 'inactive') { recommendation = 'Inspect power / communications and bring the asset back online.'; suggestedType = 'corrective'; }
    else recommendation = 'No action needed — keep monitoring.';
    const suggestedPriority = level === 'critical' ? 'critical' : level === 'high' ? 'high' : level === 'medium' ? 'medium' : 'low';
    return { score, level, reasons, recommendation, suggestedPriority, suggestedType, failInDays: failIn, covered };
}

/**
 * Grid-wide brief for the Live Grid AI panel.
 * @param {Array} assets enriched assets (risk merged)
 * @param {{ zones?: Array, liveAlerts?: Record<string,object>, now?: number, wsConnected?: boolean }} opts
 */
export function buildGridBrief(assets = [], { zones = [], liveAlerts = {}, now = Date.now(), wsConnected } = {}) {
    const health = { optimal: 0, warning: 0, critical: 0, unknown: 0 };
    let offline = 0; let maintenance = 0; let failingSoon = 0; let mlHighRisk = 0; let healthSum = 0; let healthN = 0;
    const assessed = [];
    assets.forEach((a) => {
        const h = num(a.health_score);
        if (h === null) health.unknown++;
        else { healthSum += h; healthN++; if (h >= 80) health.optimal++; else if (h >= 40) health.warning++; else health.critical++; }
        const s = lc(a.status);
        if (s === 'offline' || s === 'inactive') offline++;
        if (s === 'maintenance') maintenance++;
        if (num(a.risk_score) !== null && num(a.risk_score) >= 70) mlHighRisk++;
        const as = assessAsset(a, now, liveAlerts[String(a.id)] || null);
        if (as.failInDays !== null && as.failInDays <= 30) failingSoon++;
        if (as.score > 0) assessed.push({ asset: a, ...as });
    });
    assessed.sort((x, y) => y.score - x.score || (num(x.asset.health_score) ?? 100) - (num(y.asset.health_score) ?? 100));
    const anomalies = Object.keys(liveAlerts).length;

    const worstZones = (zones || [])
        .filter(z => num(z.average_health_score) !== null)
        .sort((a, b) => num(a.average_health_score) - num(b.average_health_score))
        .slice(0, 3)
        .map(z => ({ id: z.id, name: z.name || `Zone ${z.id}`, avgHealth: Math.round(num(z.average_health_score) * 10) / 10, activeWorkOrders: num(z.active_work_orders) ?? 0, totalAssets: num(z.total_assets) ?? 0 }));

    // Explainable grid score
    const total = assets.length || 1;
    const deductions = [];
    const ded = (pts, why) => { if (pts > 0) deductions.push({ points: Math.round(pts), why }); };
    ded(Math.min(30, (health.critical / total) * 150), `${health.critical} asset(s) in critical health`);
    ded(Math.min(15, (health.warning / total) * 40), `${health.warning} deteriorating`);
    ded(Math.min(20, anomalies * 5), `${anomalies} live anomal${anomalies === 1 ? 'y' : 'ies'}`);
    ded(Math.min(15, failingSoon * 3), `${failingSoon} predicted to fail within 30 days`);
    ded(Math.min(10, (offline / total) * 50), `${offline} offline`);
    if (wsConnected === false) ded(5, 'Live stream disconnected');
    const score = Math.max(0, 100 - deductions.reduce((s, d) => s + d.points, 0));
    const grade = score >= 85 ? { label: 'Healthy', color: 'green' } : score >= 65 ? { label: 'Watch', color: 'yellow' } : { label: 'Needs attention', color: 'red' };

    const urgent = assessed.filter(x => !x.covered && (x.level === 'critical' || x.level === 'high'));
    const headline = !assets.length
        ? 'No mapped assets yet.'
        : urgent.length
            ? `${urgent.length} asset${urgent.length === 1 ? '' : 's'} need${urgent.length === 1 ? 's' : ''} action. Top: ${urgent[0].asset.name} — ${urgent[0].reasons[0]}.`
            : `Grid stable — ${health.optimal} optimal, ${maintenance} under maintenance${anomalies ? `, ${anomalies} live anomal${anomalies === 1 ? 'y' : 'ies'}` : ''}.`;

    return {
        score, grade, deductions, headline,
        totals: { assets: assets.length, offline, maintenance, failingSoon, mlHighRisk, anomalies, avgHealth: healthN ? Math.round((healthSum / healthN) * 10) / 10 : null },
        health,
        priorities: assessed.slice(0, 12),
        worstZones,
        at: new Date(now).toISOString(),
    };
}

const fmtAsset = (x) => `${x.asset.name} (${x.asset.type || 'asset'}${x.asset.zone_name ? `, ${x.asset.zone_name}` : ''}) — ${x.reasons.slice(0, 2).join('; ') || 'monitoring'}`;
const bullet = (lines) => lines.filter(Boolean).map(l => `• ${l}`).join('\n');

/** Rules-based answers (labelled "FlowSense rules" in the UI). */
export function answerGridLocally(question, brief, assets = []) {
    const q = lc(question).trim();
    const res = (topic, answer) => ({ answer, source: 'rules', topic });
    if (!brief) return res('fallback', 'The grid is still loading.');
    const t = brief.totals;

    // A specific asset by name / id
    const idMatch = q.match(/(?:asset\s*#?|#)(\d+)/);
    const named = assets.find(a => a.name && String(a.name).length >= 3 && q.includes(lc(a.name)))
        || (idMatch ? assets.find(a => String(a.id) === idMatch[1]) : null);
    if (named) {
        const as = assessAsset(named);
        return res('asset', [`${named.name} (${named.type || 'asset'}, ${named.status || 'unknown status'})`, bullet([
            named.health_score != null ? `Health ${named.health_score}%` : 'No health score yet',
            named.risk_score != null ? `ML failure risk ${named.risk_score}%` : null,
            ...as.reasons,
            `Recommendation: ${as.recommendation}`,
        ])].join('\n'));
    }
    if (/fail|risk|predict|rul|break/.test(q)) {
        const list = brief.priorities.filter(x => x.failInDays !== null || x.asset.risk_score != null).slice(0, 6);
        return res('risk', list.length ? [`${t.failingSoon} asset(s) predicted to fail within 30 days, ${t.mlHighRisk} with ML risk ≥ 70%:`, bullet(list.map(fmtAsset))].join('\n') : 'No assets are predicted to fail soon and no high ML risk is reported.');
    }
    if (/anomal|alert|alarm|sensor/.test(q)) {
        const list = brief.priorities.filter(x => x.reasons.some(r => r.startsWith('Live'))).slice(0, 6);
        return res('anomalies', t.anomalies ? [`${t.anomalies} live anomal${t.anomalies === 1 ? 'y' : 'ies'} on the grid:`, bullet(list.map(fmtAsset))].join('\n') : 'No live anomalies in the last two minutes.');
    }
    if (/offline|down|inactive/.test(q)) {
        const list = assets.filter(a => ['offline', 'inactive'].includes(lc(a.status))).slice(0, 8);
        return res('offline', list.length ? [`${t.offline} asset(s) offline:`, bullet(list.map(a => `${a.name} (${a.type || 'asset'})`))].join('\n') : 'No assets are offline.');
    }
    if (/zone|district|area/.test(q)) {
        return res('zones', brief.worstZones.length ? ['Zones with the lowest average health:', bullet(brief.worstZones.map(z => `${z.name}: ${z.avgHealth}% avg health, ${z.activeWorkOrders} active work orders, ${z.totalAssets} assets`))].join('\n') : 'Zone analytics are not loaded — turn on district boundaries and recalculate.');
    }
    if (/work ?order|dispatch|create|ticket/.test(q)) {
        return res('how_to', 'Click an asset → "Generate Work Order" creates a typed predictive / corrective job with an Ollama brief (or "Open in form" to review it first). Assets with an active work order are locked to prevent duplicates.');
    }
    if (/ai lens|colou?r|legend|health score/.test(q)) {
        return res('lens', 'AI Lens colours markers by ML health: green 80–100% optimal, orange 40–79% deteriorating, red below 40% critical. Dashed purple rings = ML failure risk ≥ 70%; pulsing red rings = live anomalies from the WebSocket.');
    }
    if (/tile|basemap|map style|satellite/.test(q)) {
        return res('tiles', 'Use the layers button to switch basemap: CARTO Dark Matter / Voyager / Positron (FlowSense CARTO key), satellite, hybrid, Esri streets, terrain or OpenStreetMap. Your choice is remembered.');
    }
    const top = brief.priorities.filter(x => !x.covered).slice(0, 5);
    return res('summary', [
        `Grid score ${brief.score}/100 (${brief.grade.label}). ${brief.headline}`,
        bullet([
            `${t.assets} mapped assets · avg health ${t.avgHealth ?? '—'}%`,
            `${brief.health.critical} critical · ${brief.health.warning} deteriorating · ${t.offline} offline · ${t.maintenance} under maintenance`,
            `${t.failingSoon} predicted to fail ≤ 30 days · ${t.anomalies} live anomalies`,
        ]),
        top.length ? `Top priorities:\n${bullet(top.map(fmtAsset))}` : '',
    ].filter(Boolean).join('\n'));
}

/** Compact snapshot for Ollama (kept well under the backend 8 000-char cap). */
export function buildGridAiContext(brief, selectedAsset = null) {
    if (!brief) return { page: 'live_grid' };
    const ctx = {
        page: 'Live Grid (geospatial command center map)',
        grid: { score: brief.score, grade: brief.grade.label, headline: brief.headline, ...brief.totals, health_split: brief.health, deductions: brief.deductions.map(d => `${d.why} (-${d.points})`) },
        top_priorities: brief.priorities.slice(0, 10).map(x => ({
            name: x.asset.name, type: x.asset.type, zone: x.asset.zone_name || null, status: x.asset.status,
            health: x.asset.health_score ?? null, ml_risk: x.asset.risk_score ?? null, priority: x.level, reasons: x.reasons.slice(0, 3), recommendation: x.recommendation,
        })),
        worst_zones: brief.worstZones,
    };
    if (selectedAsset) {
        const a = selectedAsset;
        ctx.selected_asset = {
            id: a.id, name: a.name, type: a.type, status: a.status, zone: a.zone_name || null,
            health: a.health_score ?? null, ml_risk: a.risk_score ?? null, ml_finding: a.risk_description || null,
            ml_prescription: a.prescriptive_action?.action || null, predicted_failure_date: a.predicted_failure_date || null,
            remaining_useful_life_days: a.remaining_useful_life ?? null, last_psi: a.last_psi ?? null,
            installed: a.installation_date || null, last_service: a.last_maintenance_date || null,
        };
    }
    return ctx;
}

export const GRID_QUESTIONS = [
    'Which assets need action right now?',
    'Which assets are predicted to fail?',
    'Are there live sensor anomalies?',
    'Which zones are in the worst condition?',
    'Give me a breakdown of the grid',
];
